import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { MonitoringCategorySection } from "@domain/project/monitoring-product-list/monitoring-category-section";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { MonitoringGroupSection } from "@domain/project/monitoring-product-list/monitoring-group-section";
import {
  AdditionalMonitoringConfigSection,
  MonitoringBomServiceRow,
} from "@domain/project/monitoring-product-list/monitoring-product-list.service";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { buildMonitoringAdditionalCategoryDisplayData } from "@domain/project/product-list/product-list-display-data.util";
import { ProductListService } from "@domain/project/product-list/product-list.service";
import Excel from "exceljs";
import { createProductListTheme } from "./export-product-list.theme";

type MonitoringExportRowData = {
  positionId?: string;
  matNr: string;
  quantity: number;
  name: string;
};

export class ExportMonitoringProductListBuilder {
  private readonly LINE_BREAK = " \r\n";
  private readonly theme = createProductListTheme();

  constructor(private productListService: ProductListService) {}

  build(
    worksheet: Excel.Worksheet,
    project: Project,
    monitoringGroupSections: MonitoringGroupSection[],
    serviceRows: MonitoringBomServiceRow[],
    additionalSections: AdditionalMonitoringConfigSection[] = [],
  ) {
    this.writeSheetFormat(worksheet);
    this.writeProjectInfo(worksheet, project);
    this.writeHeader(worksheet);

    let rowIndex = 5;
    rowIndex = this.addMonitoringSections(monitoringGroupSections, rowIndex, worksheet);
    rowIndex = this.addAdditionalSections(additionalSections, rowIndex, worksheet);
    this.addServices(worksheet, rowIndex, serviceRows);
  }

  private writeSheetFormat(worksheet: Excel.Worksheet) {
    worksheet.getRow(1).height = 13.5;
    worksheet.getRow(2).height = 13.5;
    worksheet.getRow(4).height = 25.65;
    worksheet.getColumn(1).width = 9;
    worksheet.getColumn(2).width = 16;
    worksheet.getColumn(3).width = 9;
    worksheet.getColumn(4).width = 64;
    worksheet.getColumn(1).alignment = { horizontal: "right" };
    worksheet.getColumn(2).alignment = { horizontal: "center" };
    worksheet.getColumn(3).alignment = { horizontal: "right" };
  }

  private writeProjectInfo(worksheet: Excel.Worksheet, project: Project) {
    worksheet.mergeCells("A1:C1");
    worksheet.mergeCells("A2:C2");
    worksheet.mergeCells("A3:C3");

    this.fillRange(worksheet, 1, 1, 3, this.theme.fills.header);
    this.fillRange(worksheet, 2, 1, 3, this.theme.fills.header);
    [1, 2, 3].forEach((row) => this.applyHorizontalBorder(worksheet, row, 1, 4, this.theme.borders.thick));
    this.applyLeftBorder(worksheet.getCell("A1"), this.theme.borders.thick);
    this.applyLeftBorder(worksheet.getCell("A2"), this.theme.borders.thick);
    this.applyRightBorder(worksheet.getCell("D1"), this.theme.borders.thick);
    this.applyRightBorder(worksheet.getCell("D2"), this.theme.borders.thick);
    this.applyRightBorder(worksheet.getCell("D3"), this.theme.borders.thick);

    worksheet.getRow(1).font = this.theme.fonts.bold;
    worksheet.getRow(2).font = this.theme.fonts.bold;
    worksheet.getCell("A1").value = $localize`:@@monitoring.productListExport.projectName:Project name:`;
    worksheet.getCell("D1").value = project.name;
    worksheet.getCell("A2").value = $localize`:@@monitoring.productListExport.customerNumber:KD no:`;
    worksheet.getCell("D2").value = project.customer.customerNumber;
    worksheet.getCell("A3").value = $localize`:@@monitoring.productListExport.address:Address:`;
    worksheet.getCell("D3").value = this.createAddressValue(project);

    ["A1", "A2", "A3"].forEach((cellAddress) => {
      worksheet.getCell(cellAddress).font = this.theme.fonts.bold;
      worksheet.getCell(cellAddress).alignment = { horizontal: "right", vertical: "middle" };
    });
    ["D1", "D2"].forEach((cellAddress) => {
      worksheet.getCell(cellAddress).alignment = { vertical: "middle" };
    });
    worksheet.getCell("A3").alignment = { horizontal: "right", vertical: "top" };
    worksheet.getCell("D3").alignment = { wrapText: true, vertical: "top" };
    worksheet.getCell("D3").font = this.theme.fonts.normal;
  }

  private writeHeader(worksheet: Excel.Worksheet) {
    worksheet.getRow(4).font = this.theme.fonts.bold;
    ["A4", "B4", "C4", "D4"].forEach((cellAddress) => {
      worksheet.getCell(cellAddress).alignment = { vertical: "middle", horizontal: "center" };
    });

    worksheet.getCell("A4").value = $localize`:@@monitoring.productListExport.column1.header:Pos.`;
    worksheet.getCell("B4").value = $localize`:@@monitoring.productListExport.column2.header:Part number`;
    worksheet.getCell("C4").value = $localize`:@@monitoring.productListExport.column3.header:Quantity`;
    worksheet.getCell("D4").value = $localize`:@@monitoring.productListExport.column4.header:Designation`;
  }

  private addMonitoringSections(groupSections: MonitoringGroupSection[], rowIndex: number, worksheet: Excel.Worksheet): number {
    const hasAnyData = groupSections.some((groupSection) =>
      [
        groupSection.patientMonitors,
        groupSection.centralStations,
        groupSection.telemetries,
        groupSection.kvms,
        groupSection.displays,
        groupSection.utilities,
      ].some((section) => this.hasCategoryData(section)),
    );

    if (hasAnyData) {
      rowIndex = this.addSectionHeader(
        worksheet,
        rowIndex,
        $localize`:@@productList.productsOnFloorPlan:Produkte auf dem Grundrissplan`,
        true,
      );
    }

    groupSections.forEach((groupSection) => {
      const categories = [
        { section: groupSection.patientMonitors, type: MonitoringCategory.PATIENT_MONITORS },
        { section: groupSection.centralStations, type: MonitoringCategory.CENTRAL_STATIONS },
        { section: groupSection.telemetries, type: MonitoringCategory.TELEMETRY },
        { section: groupSection.kvms, type: MonitoringCategory.KVMS },
        { section: groupSection.displays, type: MonitoringCategory.DISPLAYS },
        { section: groupSection.utilities, type: MonitoringCategory.UTILITY },
      ];

      categories.forEach(({ section, type }) => {
        if (this.hasCategoryData(section)) {
          rowIndex = this.addSectionHeader(
            worksheet,
            rowIndex,
            `${groupSection.name} - ${this.getMonitoringProductSectionHeadline(type)}`,
          );
          rowIndex = this.addMonitoringProductSection(section, worksheet, rowIndex);
        }
      });
    });

    return rowIndex;
  }

  private addMonitoringProductSection(
    section: MonitoringCategorySection<
      FloorplanPatientMonitor | FloorplanCentralStation | FloorplanTelemetry | FloorplanKvm | FloorplanDisplay | FloorplanUtility,
      FloorplanSimplePlaceholder,
      | PatientMonitorConfiguration
      | CentralStationConfiguration
      | TelemetryConfiguration
      | KvmConfiguration
      | DisplayConfiguration
      | UtilityConfiguration,
      MonitoringConfigProductMap
    >,
    worksheet: Excel.Worksheet,
    rowIndex: number,
  ): number {
    section.mappings.forEach((mapping) => {
      rowIndex = this.addMonitoringProductData(mapping.data, worksheet, rowIndex, mapping.placedProductsCount);

      section.devices.forEach((device) => {
        if (device.products) {
          device.products.forEach((product) => {
            if (product.id && this.monitoringProductBelongsToConfig(device, mapping)) {
              rowIndex = this.addMonitoringPlaceholderProducts([product], worksheet, rowIndex, 1);
            }
          });
        }
      });
    });

    section.placeholders.forEach((placeholder) => {
      const placeholderProducts = placeholder.products.filter((product) => product.id);
      if (placeholderProducts.length > 0) {
        rowIndex = this.addMonitoringPlaceholderProducts(placeholderProducts, worksheet, rowIndex, 1);
      }
    });

    const unspecifiedPlaceholderCount = this.productListService.countUnspecifiedPlaceholders(section.placeholders);
    if (unspecifiedPlaceholderCount > 0) {
      rowIndex = this.writeMonitoringRow(
        worksheet,
        rowIndex,
        section.placeholderPositionPrefix,
        "",
        unspecifiedPlaceholderCount,
        $localize`:@@productList.placeholder.description:Platzhalter (noch zu konfigurieren)`,
      );
    }

    return rowIndex;
  }

  private addMonitoringProductData(
    productData: MonitoringExportRowData[],
    worksheet: Excel.Worksheet,
    rowIndex: number,
    placedProductCount: number,
  ): number {
    productData.forEach((data) => {
      rowIndex = this.writeMonitoringRow(
        worksheet,
        rowIndex,
        data.positionId,
        data.matNr,
        data.quantity * placedProductCount,
        data.name,
      );
    });

    return rowIndex;
  }

  private addMonitoringPlaceholderProducts(
    products: PlaceholderProduct[],
    worksheet: Excel.Worksheet,
    rowIndex: number,
    quantity: number,
  ): number {
    products.forEach((product) => {
      rowIndex = this.writeMonitoringRow(worksheet, rowIndex, product.positionId, product.id, quantity, product.name);
    });

    return rowIndex;
  }

  private addAdditionalSections(
    additionalSections: AdditionalMonitoringConfigSection[],
    rowIndex: number,
    worksheet: Excel.Worksheet,
  ): number {
    if (additionalSections.length === 0) {
      return rowIndex;
    }

    const categories = buildMonitoringAdditionalCategoryDisplayData(false, additionalSections, {});
    rowIndex = this.addSectionHeader(
      worksheet,
      rowIndex,
      $localize`:@@productList.additionalProducts:Zusätzliche Produkte`,
      true,
    );

    categories.forEach((category) => {
      rowIndex = this.addSectionHeader(worksheet, rowIndex, category.label);
      category.configs.forEach((config) => {
        rowIndex = this.writeMonitoringRow(
          worksheet,
          rowIndex,
          config.positionNumber,
          config.mainProductId ?? "",
          config.savedQuantity,
          config.name,
        );
        rowIndex = this.addMonitoringProductData(config.subproducts, worksheet, rowIndex, config.savedQuantity);
      });
    });

    return rowIndex;
  }

  private addServices(worksheet: Excel.Worksheet, rowIndex: number, serviceRows: MonitoringBomServiceRow[]) {
    if (serviceRows.length === 0) {
      return;
    }

    rowIndex = this.addSectionHeader(worksheet, rowIndex, $localize`:@@monitoring.productListExport.services:Services`, true);
    serviceRows.forEach((serviceRow) => {
      rowIndex = this.addSectionHeader(worksheet, rowIndex, serviceRow.name);
      rowIndex = this.writeMonitoringRow(
        worksheet,
        rowIndex,
        serviceRow.position.toString(),
        serviceRow.partNumber,
        1,
        serviceRow.notes,
      );
    });
  }

  private addSectionHeader(worksheet: Excel.Worksheet, rowIndex: number, header: string, isRootSection = false) {
    worksheet.getRow(rowIndex).font = isRootSection ? this.theme.fonts.rootBold : this.theme.fonts.bold;
    this.fillRange(worksheet, rowIndex, 1, 4, this.theme.fills.header);
    worksheet.getCell(rowIndex, 4).value = header;
    return rowIndex + 1;
  }

  private writeMonitoringRow(
    worksheet: Excel.Worksheet,
    rowIndex: number,
    positionId: string | undefined,
    partNumber: string,
    quantity: number,
    designation: string,
  ) {
    worksheet.getRow(rowIndex).values = [positionId, partNumber, quantity, designation];
    worksheet.getRow(rowIndex).font = this.theme.fonts.normal;
    worksheet.getCell(rowIndex, 4).alignment = { wrapText: true, vertical: "top" };
    return rowIndex + 1;
  }

  private createAddressValue(project: Project) {
    return (
      (project.contactPerson.firstname ? project.contactPerson.firstname + " " : "") +
      (project.contactPerson.lastname ? project.contactPerson.lastname + this.LINE_BREAK : "") +
      (project.contactPerson.department ? project.contactPerson.department + this.LINE_BREAK : "") +
      (project.contactPerson.email ? project.contactPerson.email + this.LINE_BREAK : "") +
      (project.contactPerson.phoneNumber ? "Tel: " + project.contactPerson.phoneNumber + this.LINE_BREAK + this.LINE_BREAK : "") +
      (project.customer.name ? project.customer.name + this.LINE_BREAK : "") +
      (project.customer.street ? project.customer.street + this.LINE_BREAK : "") +
      (project.customer.zipCode ? project.customer.zipCode + " " : "") +
      (project.customer.city ? project.customer.city : "")
    );
  }

  private hasCategoryData(
    section:
      | MonitoringCategorySection<
          | FloorplanPatientMonitor
          | FloorplanCentralStation
          | FloorplanTelemetry
          | FloorplanKvm
          | FloorplanDisplay
          | FloorplanUtility,
          FloorplanSimplePlaceholder,
          | PatientMonitorConfiguration
          | CentralStationConfiguration
          | TelemetryConfiguration
          | KvmConfiguration
          | DisplayConfiguration
          | UtilityConfiguration,
          MonitoringConfigProductMap
        >
      | undefined,
  ) {
    return !!section && (section.mappings.length > 0 || section.placeholders.length > 0 || section.devices.length > 0);
  }

  private getMonitoringProductSectionHeadline(type: MonitoringCategory) {
    switch (type) {
      case MonitoringCategory.PATIENT_MONITORS:
        return $localize`:@@monitoring.patientMonitors:Patienten Monitore`;
      case MonitoringCategory.CENTRAL_STATIONS:
        return $localize`:@@monitoring.centralStations:Central Stations`;
      case MonitoringCategory.TELEMETRY:
        return $localize`:@@monitoring.telemetries:Telemetries`;
      case MonitoringCategory.KVMS:
        return $localize`:@@monitoring.kvms:KVMs`;
      case MonitoringCategory.DISPLAYS:
        return $localize`:@@monitoring.displays:Displays`;
      case MonitoringCategory.UTILITY:
        return $localize`:@@monitoring.utilities:Utilities`;
      default:
        return "";
    }
  }

  private monitoringProductBelongsToConfig(
    product:
      FloorplanPatientMonitor | FloorplanCentralStation | FloorplanTelemetry | FloorplanKvm | FloorplanDisplay | FloorplanUtility,
    mapping: MonitoringConfigProductMap,
  ) {
    return product.config.id === mapping.config.id;
  }

  private fillRange(worksheet: Excel.Worksheet, row: number, fromColumn: number, toColumn: number, fill: Excel.Fill) {
    for (let column = fromColumn; column <= toColumn; column += 1) {
      worksheet.getCell(row, column).fill = fill;
    }
  }

  private applyHorizontalBorder(
    worksheet: Excel.Worksheet,
    row: number,
    fromColumn: number,
    toColumn: number,
    border: Excel.Border,
  ) {
    for (let column = fromColumn; column <= toColumn; column += 1) {
      worksheet.getCell(row, column).border = {
        ...worksheet.getCell(row, column).border,
        top: border,
        bottom: border,
      };
    }
  }

  private applyLeftBorder(cell: Excel.Cell, border: Excel.Border) {
    cell.border = { ...cell.border, left: border };
  }

  private applyRightBorder(cell: Excel.Cell, border: Excel.Border) {
    cell.border = { ...cell.border, right: border };
  }
}
